import React, {useEffect, useState} from 'react';
import {Alert, Button, Checkbox, Form, Input, message} from 'antd';
import {LockOutlined, LoginOutlined, UserOutlined} from '@ant-design/icons';
import Cookies from 'js-cookie';
import {useLocation, useNavigate} from 'react-router-dom';
import {COMMON_API} from '../config/api';
import {encryptPassword} from '../security/passwordTransport';
import '../auth.css';

const LoginForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const urlData = new URLSearchParams(location.search);
    const returnUrl = urlData.get('return_url');
    const userToken = urlData.get('user_token');
    const userGroup = urlData.get('group');
    const [submitting, setSubmitting] = useState(false);

    document.title = '登录';

    const goByGroup = (group: string | null | undefined) => {
        switch (group) {
            case 'projectadmin':
                window.location.href = returnUrl || '/project/projectmanagement';
                Cookies.set('group', 'projectadmin');
                break;
            case 'admin':
                window.location.href = returnUrl || '/admin/keyclick';
                Cookies.set('group', 'admin');
                break;
            case 'customer':
                window.location.href = returnUrl || '/employeemanage/keyclick';
                Cookies.set('group', 'customer');
                break;
            default:
                window.location.href = returnUrl || '/admin/keyclick';
                break;
        }
    };

    useEffect(() => {
        if (userToken) {
            const expires = new Date(new Date().getTime() + 0.5 * 24 * 60 * 60 * 1000);
            Cookies.set('userToken_chattool', userToken, {expires});
            message.success('登录成功', 3);
            goByGroup(userGroup);
        }
    }, [userToken, userGroup]);

    const onFinish = async (values: any) => {
        const loginBody = new FormData();
        loginBody.append('user_name', values.user_name);
        try {
            loginBody.append('user_password', encryptPassword(values.user_password));
        } catch {
            message.error('密码加密失败，请刷新页面后重试');
            return;
        }
        loginBody.append('country_code', values.country_code || '');

        setSubmitting(true);
        try {
            const response = await fetch(COMMON_API.login, {
                method: 'POST',
                body: loginBody,
            });
            const result = await response.json();

            switch (result.message) {
                case 2000: {
                    if (values.remember) {
                        const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                        Cookies.set('userToken_chattool', result.info.user_access_info, {expires, sameSite: 'lax', secure: window.location.protocol === 'https:'});
                    } else {
                        Cookies.set('userToken_chattool', result.info.user_access_info, {sameSite: 'lax', secure: window.location.protocol === 'https:'});
                    }
                    message.success('登录成功', 3);
                    goByGroup(result.info.user_group_info);
                    break;
                }
                case 2001:
                case 2002:
                    message.error(result.value);
                    break;
                default:
                    message.error(result.value || '登录失败，请稍后再试');
                    break;
            }
        } catch (error) {
            message.error('登录请求失败，请检查网络或接口配置');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="auth-page">
            <section className="auth-visual">
                <div className="auth-brand">
                    <span className="auth-brand-mark">C2</span>
                    <span>ChatV2</span>
                </div>
                <div className="auth-copy">
                    <h1>登录工作台</h1>
                    <p>管理快捷话术、知识库和客服工作流，进入后即可继续处理业务。</p>
                </div>
                <div className="auth-stats">
                    <div>
                        <strong>24h</strong>
                        <span>在线服务</span>
                    </div>
                    <div>
                        <strong>Team</strong>
                        <span>协同管理</span>
                    </div>
                </div>
            </section>

            <section className="auth-panel" aria-label="登录表单">
                <div className="auth-card">
                    <div className="auth-card-header">
                        <span className="auth-kicker">Welcome back</span>
                        <h2>账号登录</h2>
                    </div>

                    <Form
                        name="loginForm"
                        layout="vertical"
                        initialValues={{remember: true}}
                        onFinish={onFinish}
                        className="auth-form"
                    >
                        <Form.Item
                            label="邮箱/账号"
                            name="user_name"
                            rules={[{required: true, message: '请输入邮箱或账号'}]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="请输入邮箱或账号" autoComplete="username" />
                        </Form.Item>

                        <Form.Item
                            label="密码"
                            name="user_password"
                            rules={[{required: true, message: '请输入密码'}]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" autoComplete="current-password" />
                        </Form.Item>

                        <div className="auth-row">
                            <Form.Item name="remember" valuePropName="checked" className="auth-remember">
                                <Checkbox>记住登录状态</Checkbox>
                            </Form.Item>
                            <Button type="link" onClick={() => navigate('/register')} className="auth-link">
                                注册账号
                            </Button>
                        </div>

                        <Form.Item className="auth-submit">
                            <Button type="primary" htmlType="submit" icon={<LoginOutlined />} loading={submitting} block>
                                登录
                            </Button>
                        </Form.Item>
                        <Alert className="auth-security-tip" type="info" showIcon message="密码将加密提交，服务端仅保存不可逆哈希" />
                    </Form>
                </div>
            </section>
        </main>
    );
};

export default LoginForm;
